import * as stylex from "@stylexjs/stylex";
import { fonts, spacing } from "@softmax/design-system/tokens.stylex";
import type { ReactNode } from "react";
import { CoworldArt, type CoworldHeaderArt } from "./coworld-art";
import { CoworldHeaderName } from "./coworld-header-name";
import {
  CoworldHeaderStats,
  type CoworldHeaderStat,
} from "./coworld-header-stats";

export function CoworldHeaderView({
  art,
  name,
  site,
  about,
  stats,
  anchored = false,
  after,
}: {
  readonly after?: ReactNode;
  /** Expanding the about text leaves the title and art anchored in place. */
  readonly anchored?: boolean | undefined;
  readonly art: CoworldHeaderArt;
  readonly name: string;
  readonly site: string | undefined;
  readonly about: ReactNode;
  readonly stats: readonly CoworldHeaderStat[];
}) {
  return (
    <header {...stylex.props(styles.header, anchored && styles.headerTop)}>
      <CoworldArt art={art} anchored={anchored} />
      <CoworldHeaderName name={name} site={site} anchored={anchored} />
      {about === null ? null : (
        <div {...stylex.props(styles.about, anchored && styles.aboutTop)}>
          {about}
        </div>
      )}
      <CoworldHeaderStats stats={stats} anchored={anchored} />
      {anchored && after !== undefined ? (
        <div {...stylex.props(styles.afterTop)}>{after}</div>
      ) : null}
    </header>
  );
}

const wide = "@media (min-width: 640px)";

const styles = stylex.create({
  header: {
    display: "grid",
    gridTemplateColumns: {
      default: "120px minmax(0, 1fr)",
      [wide]: "194px minmax(0, 1fr)",
    },
    gridTemplateRows: { default: "none", [wide]: "1fr auto auto auto 1fr" },
    columnGap: { default: spacing.space16, [wide]: spacing.space40 },
    paddingBottom: spacing.space32,
    fontFamily: fonts.sans,
  },
  headerTop: {
    gridTemplateColumns: "120px minmax(0, 1fr)",
    gridTemplateRows: "auto auto auto auto",
    columnGap: spacing.space20,
    paddingBottom: 0,
  },
  about: {
    gridColumnStart: { default: 1, [wide]: 2 },
    gridColumnEnd: { default: 3, [wide]: "auto" },
    gridRowStart: { default: 2, [wide]: 3 },
    marginTop: 14,
    minWidth: 0,
    display: { default: "block", ":empty": "none" },
  },
  aboutTop: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 2,
    marginTop: spacing.space20,
  },
  afterTop: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    gridRowStart: 4,
    marginTop: spacing.space24,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: spacing.space20,
    alignSelf: "start",
  },
});
